import { CampaignSourceObject, SourceQueryOptions, useSource } from './useSource'

/**
 * Whether a source's analysis is still being produced — i.e. whether usePollSource
 * should keep polling. The card's loading state is derived from the same predicate:
 * while this is true we show a loader, and once it flips false we show either the
 * analysis or a failed state.
 *
 * `enqueue_scraping` runs synchronously when the source is created, so a freshly
 * added source always reports a pending scraping on the first fetch — there is no
 * window where a readable source looks done-with-no-analysis. Polling therefore only
 * resolves once the source either has an analysis, or scraping has finished (or
 * failed) without producing any source materials.
 */
export const isSourceAnalysisPending = (source?: CampaignSourceObject): boolean => {
  if (!source) return true

  const materials = source.source_materials ?? []
  if (materials.some((material) => material.source_material_analysis != null)) return false // analysis ready

  if (source.pending_or_started_source_material_scraping != null) return true // still scraping

  if (materials.length === 0) return false // unreadable — scraping produced nothing

  return true // materials extracted, analysis not complete yet
}

/**
 * Polls GET /w/{workspaceId}/sources/{id}?type={type} every 2s while the source
 * analysis is pending, and stops once it resolves.
 *
 * Consumers can access the analysis via:
 *   `data?.source_materials?.[0]?.source_material_analysis`
 * and whether it's still loading via `isSourceAnalysisPending(data)`.
 */
export const usePollSource = (id: number, type: CampaignSourceObject['type'], options?: SourceQueryOptions) => {
  return useSource(id, type, {
    refetchInterval: (data) => (isSourceAnalysisPending(data) ? 2000 : false),
    ...options,
  })
}
